import { useState } from 'react';

const ElementIcon = ({ element }: { element: string }) => {
  const icons = {
    fire: (
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8 0-3.31 2.69-6 6-6s6 2.69 6 6c0 4.41-3.59 8-8 8z"
          className="text-red-500"
          fill="currentColor"
        />
        <path
          d="M12.5 7.5c-.76-.76-1.86-1.11-2.83-.9-.95.2-1.73.98-1.93 1.93-.2.97.14 2.07.9 2.83L12 14.72l3.36-3.36c.76-.76 1.11-1.86.9-2.83-.2-.95-.98-1.73-1.93-1.93-.97-.2-2.07.14-2.83.9z"
          className="text-red-500"
          fill="currentColor"
        />
      </svg>
    ),
    water: (
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path
          d="M12 2L5 12c0 3.87 3.13 7 7 7s7-3.13 7-7l-7-10zM12 17c-2.76 0-5-2.24-5-5 0-1.94 1.11-3.62 2.72-4.44C10.55 7.19 11.26 7 12 7c.74 0 1.45.19 2.28.56C15.89 8.38 17 10.06 17 12c0 2.76-2.24 5-5 5z"
          className="text-blue-500"
          fill="currentColor"
        />
      </svg>
    ),
    earth: (
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
          className="text-emerald-500"
          fill="currentColor"
        />
      </svg>
    ),
    wind: (
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path
          d="M14.5 17c0 1.65-1.35 3-3 3s-3-1.35-3-3h2c0 .55.45 1 1 1s1-.45 1-1-.45-1-1-1H2v-2h9.5c1.65 0 3 1.35 3 3zM19 6.5C19 4.57 17.43 3 15.5 3S12 4.57 12 6.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S17.33 8 16.5 8H2v2h14.5c1.93 0 3.5-1.57 3.5-3.5z"
          className="text-cyan-500"
          fill="currentColor"
        />
      </svg>
    ),
  };

  return (
    <div className="w-full h-full">
      {icons[element as keyof typeof icons]}
    </div>
  );
};

const RoadmapItem = ({
  phase,
  date,
  items,
  avatar,
  isLeft,
  isLastItem,
  isActive,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isNotLeft = !isLeft;

  // Extract element from avatar path
  const element = avatar.split('/').pop()?.replace('.svg', '') || 'fire';

  return (
    <div
      className={`flex flex-col ${isNotLeft && "items-end"} ${isLastItem && "items-center"}
        transform transition-all duration-500 hover:scale-[1.02]`}
    >
      <div
        className={
          !isLastItem
            ? ` 
          flex flex-col md:flex-row ${isLeft ? "md:flex-row" : "md:flex-row-reverse"}
            md:items-start md:gap-12 gap-4 mb-32 relative group`
            : "flex flex-col items-center justify-center"
        }
      >
        {/* Connection Line */}
        <div className={`absolute top-1/2 ${isLeft ? 'right-full' : 'left-full'} w-16 h-px 
          bg-gradient-to-${isLeft ? 'r' : 'l'} from-primary-500/50 to-transparent 
          transform -translate-y-1/2 md:block hidden
          after:content-[''] after:absolute after:w-2 after:h-2 after:rounded-full 
          after:bg-primary-500/50 after:${isLeft ? 'right' : 'left'}-0 after:top-1/2 after:-translate-y-1/2`} />

        {/* Avatar Container with Element Icon */}
        <div
          className={`relative w-28 h-28 rounded-full p-1 cursor-pointer group
            transform transition-all duration-500 hover:scale-110
            ${isActive ? 'bg-gradient-to-br from-primary-500 to-primary-500' : 'bg-gray-700'}
            before:content-[''] before:absolute before:inset-0 before:rounded-full 
            before:bg-gradient-to-br before:from-primary-500/20 before:to-primary-500/20 
            before:z-0 before:opacity-0 hover:before:opacity-100 before:transition-opacity`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/5 to-transparent opacity-0 
            group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative w-full h-full rounded-full overflow-hidden bg-gray-900 p-4">
            <ElementIcon element={element} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        </div>

        <div className={`flex-1 pt-12`}>
          <div className={`flex ${isLastItem && "justify-center"}`}>
            <div className="group/phase relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-primary-500 
                rounded-2xl blur-lg opacity-40 group-hover/phase:opacity-75 transition-opacity duration-300" />
              <div className="relative bg-gray-900/90 rounded-xl px-8 py-4 
                backdrop-blur-sm border border-white/5">
                <span className="font-bold text-transparent bg-clip-text 
                  bg-gradient-to-r from-primary-400 to-primary-400
                  text-lg tracking-wide">
                  {phase}
                </span>
                <div className="absolute -right-4 -top-4 w-12 h-12 rounded-full 
                  bg-gradient-to-br from-primary-500 to-primary-500
                  p-[1px] shadow-lg shadow-primary-500/30">
                  <div className="w-full h-full rounded-full bg-gray-900 flex items-center justify-center
                    text-primary-400 font-bold transform transition-transform duration-300
                    group-hover/phase:scale-110 group-hover/phase:rotate-12">
                    {date}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`w-full xl:w-[450px] transform transition-all duration-500 
            ${isExpanded ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-90 -translate-y-2'}`}>
            <ul className={`mt-8 space-y-6 text-gray-200 ${isLeft ? "md:pl-8" : "md:pr-8"} pl-2 pr-2`}>
              {items.map((item, index) => (
                <li 
                  key={index} 
                  className="group/item relative"
                >
                  <div className="absolute -inset-4 rounded-xl bg-gradient-to-r from-primary-500/5 to-transparent 
                    opacity-0 group-hover/item:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex items-start gap-4 p-4 rounded-xl
                    bg-gradient-to-r from-gray-800/30 to-transparent
                    hover:from-gray-800/50 transition-all duration-300
                    border border-white/5">
                    <div className="mt-2 w-2 h-2 rounded-full bg-gradient-to-br from-primary-500 to-primary-500
                      flex-shrink-0 group-hover/item:scale-150 transition-all duration-300
                      shadow-md shadow-primary-500/30" />
                    <span className="transition-all duration-300 group-hover/item:text-primary-400">
                      {item}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapItem;
